/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.viewmodel

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import systems.frictionless.impulse.rest.client.RestClient
import systems.frictionless.impulse.rest.client.model.Authentication
import systems.frictionless.impulse.rest.client.model.RestResponse

/**
 * The change password flow goes through a series of steps:
 * 1. Overview of account delete steps
 * 2. Sign in
 * 3. User confirms account delete again
 * 4. Confirmation that account was deleted
 */
class DeleteAccountViewModel(private val restClient: RestClient) {
    private val mutableState = MutableStateFlow<State>(State.Overview)

    val state: StateFlow<State> = mutableState

    // There is room to automate this method, so that the UI doesn't have to check before making state transitions
    fun transition() {
        when (mutableState.value) {
            State.Overview -> mutableState.tryEmit(State.SignIn(LoginViewModel(restClient)))
            is State.SignIn -> mutableState.tryEmit(State.Delete(restClient))
            is State.Delete -> mutableState.tryEmit(State.Done)
            is State.Done -> throw AssertionError()
        }
    }

    sealed class State {
        object Overview : State()

        class SignIn(val loginViewModel: LoginViewModel) : State()

        class Delete(private val restClient: RestClient) : State() {
            private val mutableState =
                MutableStateFlow<NetworkViewModelState<Authentication, Unit, Unit>>(NetworkViewModelState.Idle())

            val state: StateFlow<NetworkViewModelState<Authentication, Unit, Unit>> = mutableState

            /**
             * @param inScope Scope to use for requests to the server.
             * @param authentication Authentication to use for requesting account delete.
             * Note: [state] is expected to be Idle when when this method is called.
             */
            fun startDeleteRequest(
                inScope: CoroutineScope,
                authentication: Authentication
            ) {
                check(state.value is NetworkViewModelState.Idle || state.value is NetworkViewModelState.Failure)
                mutableState.tryEmit(NetworkViewModelState.Busy())

                inScope.launch {
                    val newState: NetworkViewModelState<Authentication, Unit, Unit> =
                        when (val restResponse = restClient.deleteUser(authentication)) {
                            is RestResponse.Success -> NetworkViewModelState.Complete(Unit)
                            is RestResponse.Failure -> NetworkViewModelState.Failure(authentication, restResponse)
                        }

                    mutableState.tryEmit(newState)
                }
            }
        }

        object Done : State()
    }
}
